import { SPThunkAction } from "common/ReduxUtils";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { getSellerId } from "common/user/UserSelectors";
import {
  CreateOrderRequestV2,
  CreateShipmentRequestV2,
  OrderResponse,
  QuantityUom,
  Source,
} from "@deliverr/replenishment-client";
import { FeatureName, getFeatureSelector } from "common/Split";
import { InventoryPool } from "@deliverr/business-types";
import { buildLineItems, normalizeDestinationAddress } from "./utils/replenishmentOrderUtils";
import { replenishmentClient } from "Clients";
import { transferCreateSetReplenishmentOrder } from "transfers/create/actions";
import { isEmpty } from "lodash";
import { throwIfEmpty } from "common/utils/ValidationUtils";

/**
 * Creates a draft replenishment order.
 * @returns A thunk action that creates the draft order and updates the store.
 */
export const createDraftReplenishmentOrder =
  (): SPThunkAction<Promise<OrderResponse>> => async (dispatch, getState) => {
    const state = getState();

    const {
      distributionChannel,
      destinationAddress,
      destinationWarehouseId,
      selectedProducts,
      selectedProductsLotNumbers,
      isEachPickingOrder,
      poNumber,
      warehouseSelection,
      prepOptions,
      retailer,
    } = selectTransferCreate(state);

    throwIfEmpty(distributionChannel, "distributionChannel");

    const sellerId = getSellerId(state);
    const originWarehouseId = warehouseSelection.selectedWarehouseId;

    const lineItems = buildLineItems(selectedProducts, selectedProductsLotNumbers);

    const shipment: CreateShipmentRequestV2 | undefined = isEmpty(destinationAddress)
      ? undefined
      : {
          destinationWarehouseAddress: normalizeDestinationAddress(destinationAddress),
          destinationWarehouseId,
          shipmentAttributes: {
            purchaseOrderNumber: poNumber,
            retailer,
          },
        };

    const isDefaultPoolB2BMerchant = getFeatureSelector(FeatureName.DefaultPoolB2BMerchant)(state);

    const createOrderRequest: CreateOrderRequestV2 = {
      distributionChannel,
      originWarehouseId,
      sellerId,
      isDraft: true,
      source: Source.SELLER_PORTAL,
      externalId: poNumber,
      prepOptions,
      quantityUom: isEachPickingOrder ? QuantityUom.EACH : QuantityUom.CASE,
      inventoryPool: isDefaultPoolB2BMerchant ? InventoryPool.DEFAULT : InventoryPool.STORAGE,
      lineItems,
      validateAddress: false, // Address already validated
      shipment,
    };

    const order = (await replenishmentClient.createOrder(createOrderRequest)).value;

    dispatch(transferCreateSetReplenishmentOrder(order));

    return order;
  };
